import { useEffect, useState, useMemo, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { MagnifyingGlass, Users as UsersIcon, ShieldCheck, ShieldSlash, MegaphoneSimple, Storefront, Crown } from "@phosphor-icons/react";

const ROLES = [
  { value: "all", label: "All roles" },
  { value: "influencer", label: "Influencers" },
  { value: "brand", label: "Brands" },
  { value: "agency_admin", label: "Agency admins" },
];

const ROLE_META = {
  influencer: { label: "Influencer", Icon: MegaphoneSimple, cls: "text-blue-300 bg-blue-500/10 border-blue-500/20" },
  brand: { label: "Brand", Icon: Storefront, cls: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20" },
  agency_admin: { label: "Agency admin", Icon: Crown, cls: "text-[#FFD700] bg-[#FFD700]/10 border-[#FFD700]/25" },
};

export default function AgencyUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (role !== "all") params.role = role;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/users", { params });
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [role, q]);
  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => ({
    total: users.length,
    influencers: users.filter((u) => u.role === "influencer").length,
    brands: users.filter((u) => u.role === "brand").length,
    active: users.filter((u) => u.is_active !== false).length,
    deactivated: users.filter((u) => u.is_active === false).length,
  }), [users]);

  const changeRole = async (u, newRole) => {
    if (newRole === u.role) return;
    try {
      const { data } = await api.patch(`/users/${u.id}`, { role: newRole });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? data : x)));
      toast.success(`${u.name} is now ${ROLE_META[newRole]?.label || newRole}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (u) => {
    try {
      const nextActive = u.is_active === false;
      const { data } = await api.patch(`/users/${u.id}`, { is_active: nextActive });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? data : x)));
      toast.success(nextActive ? `${u.name} reactivated` : `${u.name} deactivated`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeactivateTarget(null);
    }
  };

  return (
    <div className="animate-fade-in-up" data-testid="agency-users-page">
      <div className="mb-8">
        <h1 className="font-heading font-black text-4xl">Users</h1>
        <p className="text-zinc-400 mt-1">Manage influencers, brands, and agency admins.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <StatBox label="Total" value={stats.total} Icon={UsersIcon} color="text-[#FFD700]" />
        <StatBox label="Influencers" value={stats.influencers} Icon={MegaphoneSimple} color="text-blue-400" />
        <StatBox label="Brands" value={stats.brands} Icon={Storefront} color="text-emerald-400" />
        <StatBox label="Active" value={stats.active} Icon={ShieldCheck} color="text-emerald-400" />
        <StatBox label="Deactivated" value={stats.deactivated} Icon={ShieldSlash} color="text-rose-400" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="users-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by name, email, or company…"
            className="pl-9 bg-[#0a0a0a] border-white/10 h-11"
          />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="users-role-filter" className="w-[180px] bg-[#0a0a0a] border-white/10 h-11">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-black border-white/10 text-zinc-100">
            {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          onClick={load}
          data-testid="users-search-button"
          className="bg-[#FFD700] hover:bg-[#FFDF33] text-black font-bold h-11 px-5"
        >
          Search
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/10 bg-[#0a0a0a] overflow-hidden">
        <div className="hidden md:grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-4 px-5 py-3 border-b border-white/5 text-xs font-mono uppercase tracking-widest text-zinc-500">
          <div>User</div>
          <div>Role</div>
          <div>Status</div>
          <div className="text-right">Actions</div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-zinc-500 text-sm font-mono">Loading…</div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm">No users found.</div>
        ) : users.map((u) => {
          const meta = ROLE_META[u.role] || { label: u.role, Icon: UsersIcon, cls: "text-zinc-300 bg-zinc-800/50 border-zinc-700" };
          const isSelf = u.id === me?.id;
          const active = u.is_active !== false;
          return (
            <div
              key={u.id}
              data-testid={`user-row-${u.id}`}
              className="grid md:grid-cols-[1.6fr_1fr_1fr_1fr] gap-3 md:gap-4 px-5 py-4 border-b border-white/5 last:border-b-0 items-start md:items-center hover:bg-white/[0.02]"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-zinc-800 flex-shrink-0 flex items-center justify-center overflow-hidden">
                  {u.picture ? (
                    <img src={u.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-heading font-bold text-sm text-zinc-300">
                      {(u.name || "?").slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-heading font-bold text-sm truncate flex items-center gap-1.5">
                    {u.name}
                    {isSelf && <span className="text-[10px] font-mono uppercase text-[#FFD700] bg-[#FFD700]/10 border border-[#FFD700]/25 px-1.5 py-0.5 rounded">you</span>}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{u.email}{u.company ? ` · ${u.company}` : ""}</div>
                </div>
              </div>

              <div>
                <Select
                  value={u.role}
                  onValueChange={(v) => changeRole(u, v)}
                  disabled={isSelf}
                >
                  <SelectTrigger data-testid={`role-select-${u.id}`} className="bg-[#111] border-white/10 h-9 w-[170px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-black border-white/10 text-zinc-100">
                    <SelectItem value="influencer">Influencer</SelectItem>
                    <SelectItem value="brand">Brand</SelectItem>
                    <SelectItem value="agency_admin">Agency admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-xs font-medium font-mono uppercase tracking-wide ${
                  active ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/20" : "text-rose-300 bg-rose-500/10 border-rose-500/20"
                }`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  {active ? "Active" : "Deactivated"}
                </span>
              </div>

              <div className="md:text-right">
                {isSelf ? (
                  <span className="text-xs text-zinc-600 font-mono">—</span>
                ) : active ? (
                  <Button
                    onClick={() => setDeactivateTarget(u)}
                    data-testid={`deactivate-button-${u.id}`}
                    variant="outline"
                    size="sm"
                    className="border-rose-500/20 bg-rose-500/5 hover:bg-rose-500/10 text-rose-300 h-8"
                  >
                    <ShieldSlash size={14} className="mr-1.5" /> Deactivate
                  </Button>
                ) : (
                  <Button
                    onClick={() => toggleActive(u)}
                    data-testid={`reactivate-button-${u.id}`}
                    size="sm"
                    className="bg-[#FFD700] hover:bg-[#FFDF33] text-black font-bold h-8"
                  >
                    <ShieldCheck size={14} className="mr-1.5" /> Reactivate
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <AlertDialog open={!!deactivateTarget} onOpenChange={(v) => !v && setDeactivateTarget(null)}>
        <AlertDialogContent className="bg-[#0a0a0a] border-white/10 text-zinc-50">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading font-black text-2xl">
              Deactivate {deactivateTarget?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              They won't be able to sign in or upload/review content until you reactivate them. Existing submissions and campaigns are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deactivate-cancel" className="bg-transparent border-white/10 text-zinc-200 hover:bg-white/5">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="deactivate-confirm"
              onClick={() => toggleActive(deactivateTarget)}
              className="bg-rose-500 hover:bg-rose-600 text-white font-bold"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatBox({ label, value, Icon, color }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0a0a0a] p-4 card-hover">
      <Icon size={18} weight="duotone" className={color} />
      <div className="font-heading font-black text-2xl mt-2 leading-none">{value}</div>
      <div className="text-[10px] text-zinc-500 font-mono uppercase tracking-wide mt-2">{label}</div>
    </div>
  );
}
